/**
 * Terminal component using xterm.js.
 * Connects to the backend WebSocket for an interactive shell.
 * 
 * Cyberpunk 2077 themed with matching colors.
 */

import { useEffect, useRef, useCallback, useState } from "react";
import { Terminal as XTerm } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import { WebLinksAddon } from "@xterm/addon-web-links";
import "@xterm/xterm/css/xterm.css";

interface TerminalProps {
  wsUrl: string;
  onExit?: (code: number) => void;
}

/**
 * Build xterm theme from CSS custom properties so the terminal
 * adapts to whichever Black Wall theme is active.
 */
function getThemeFromCSS(): Record<string, string> {
  const s = getComputedStyle(document.documentElement);
  const v = (name: string, fallback: string) =>
    s.getPropertyValue(name).trim() || fallback;

  const bg = v("--bw-bg", "#0a0a0f");
  const fg = v("--bw-fg", "#e4e4f0");
  const accent = v("--bw-accent", "#00f0ff");
  const danger = v("--bw-danger", "#ff003c");
  const success = v("--bw-success", "#00ff9c");
  const warn = v("--bw-warn", "#fcee0a");
  const muted = v("--bw-surface-2", "#12121a");

  return {
    background: bg,
    foreground: fg,
    cursor: accent,
    cursorAccent: bg,
    selectionBackground: accent + "33",
    selectionForeground: fg,
    selectionInactiveBackground: accent + "22",
    black: bg,
    red: danger,
    green: success,
    yellow: warn,
    blue: "#00bcd4",
    magenta: "#ff00ff",
    cyan: accent,
    white: fg,
    brightBlack: muted,
    brightRed: danger,
    brightGreen: success,
    brightYellow: warn,
    brightBlue: "#66ccff",
    brightMagenta: "#ff66ff",
    brightCyan: accent,
    brightWhite: "#ffffff",
  };
}

type ConnectionState = "connecting" | "connected" | "disconnected" | "error";

interface ClientMessage {
  type: "input" | "resize" | "attach";
  data?: string;
  cols?: number;
  rows?: number;
  sessionId?: string;
}

interface ServerMessage {
  type: "output" | "exit" | "attached";
  data?: string;
  code?: number;
  sessionId?: string;
  reattached?: boolean;
}

// Persisted so a reconnect (tab background, network drop, phone lock) reattaches
// to the SAME durable shell rather than spawning a fresh one.
const SESSION_STORAGE_KEY = "bw-terminal-session";

export function Terminal({ wsUrl, onExit }: TerminalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<XTerm | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const sessionIdRef = useRef<string | null>(
    typeof localStorage !== "undefined"
      ? localStorage.getItem(SESSION_STORAGE_KEY)
      : null,
  );
  const [connectionState, setConnectionState] = useState<ConnectionState>("connecting");
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttempts = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;

  // Build WebSocket URL (handle relative paths)
  const getWsUrl = useCallback(() => {
    if (wsUrl.startsWith("ws://") || wsUrl.startsWith("wss://")) {
      return wsUrl;
    }
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = window.location.host;
    return `${protocol}//${host}${wsUrl}`;
  }, [wsUrl]);

  // Send message to WebSocket
  const sendMessage = useCallback((msg: ClientMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  // Handle resize
  const handleResize = useCallback(() => {
    if (fitAddonRef.current && terminalRef.current) {
      fitAddonRef.current.fit();
      const { cols, rows } = terminalRef.current;
      sendMessage({ type: "resize", cols, rows });
    }
  }, [sendMessage]);

  // Connect WebSocket
  const connect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const url = getWsUrl();
    console.log("[Terminal] Connecting to", url);
    setConnectionState("connecting");

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log("[Terminal] Connected");
      setConnectionState("connected");
      reconnectAttempts.current = 0;

      // Attach (or reattach by stored session id) — the server owns the shell
      // independently of this socket, so a reconnect resumes the same shell.
      const size = terminalRef.current
        ? { cols: terminalRef.current.cols, rows: terminalRef.current.rows }
        : {};
      sendMessage({
        type: "attach",
        sessionId: sessionIdRef.current ?? undefined,
        ...size,
      });

      terminalRef.current?.focus();
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data) as ServerMessage;

        if (msg.type === "attached") {
          // Persist the (possibly newly minted) session id for future reconnects.
          if (msg.sessionId) {
            sessionIdRef.current = msg.sessionId;
            try {
              localStorage.setItem(SESSION_STORAGE_KEY, msg.sessionId);
            } catch {
              // storage unavailable — durability degrades gracefully
            }
          }
          if (msg.reattached) {
            terminalRef.current?.writeln(
              `\r\n\x1b[36m[Reattached to running shell]\x1b[0m`,
            );
          }
          // Sync the shell to the current fit (matters on reattach).
          handleResize();
        } else if (msg.type === "output" && msg.data) {
          terminalRef.current?.write(msg.data);
        } else if (msg.type === "exit") {
          console.log("[Terminal] Shell exited with code", msg.code);
          terminalRef.current?.writeln(
            `\r\n\x1b[33m[Process exited with code ${msg.code}]\x1b[0m`,
          );
          // Shell is gone — drop the stored id so the next connect is fresh.
          sessionIdRef.current = null;
          try {
            localStorage.removeItem(SESSION_STORAGE_KEY);
          } catch {
            // ignore
          }
          onExit?.(msg.code ?? 0);
        }
      } catch (err) {
        console.error("[Terminal] Error parsing message:", err);
      }
    };

    ws.onclose = (event) => {
      console.log("[Terminal] Disconnected:", event.code, event.reason);
      setConnectionState("disconnected");
      wsRef.current = null;

      // Attempt reconnect if not a clean close
      if (event.code !== 1000 && reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
        reconnectAttempts.current++;
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 10000);
        console.log(`[Terminal] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);
        
        terminalRef.current?.writeln(`\r\n\x1b[33m[Connection lost. Reconnecting...]\x1b[0m`);
        
        reconnectTimeoutRef.current = setTimeout(connect, delay);
      } else if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
        terminalRef.current?.writeln(`\r\n\x1b[31m[Max reconnection attempts reached. Refresh to retry.]\x1b[0m`);
        setConnectionState("error");
      }
    };

    ws.onerror = (error) => {
      console.error("[Terminal] WebSocket error:", error);
      setConnectionState("error");
    };
  }, [getWsUrl, sendMessage, onExit, handleResize]);

  // Initialize terminal
  useEffect(() => {
    if (!containerRef.current) return;

    // Create terminal with theme-aware colors
    const term = new XTerm({
      theme: getThemeFromCSS(),
      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
      fontSize: 14,
      fontWeight: "400",
      fontWeightBold: "700",
      lineHeight: 1.2,
      cursorBlink: true,
      cursorStyle: "block",
      allowProposedApi: true,
      scrollback: 10000,
      convertEol: true,
    });

    // Let Ctrl+C (with selection) and Ctrl+V pass through to the browser
    // so native copy/paste works. Without a selection, Ctrl+C sends SIGINT.
    term.attachCustomKeyEventHandler((e: KeyboardEvent) => {
      if (e.type !== "keydown") return true;
      if ((e.ctrlKey || e.metaKey) && e.key === "v") return false;
      if ((e.ctrlKey || e.metaKey) && e.key === "c" && term.hasSelection()) return false;
      return true;
    });
    terminalRef.current = term;

    // Add fit addon
    const fitAddon = new FitAddon();
    fitAddonRef.current = fitAddon;
    term.loadAddon(fitAddon);

    // Add web links addon
    const webLinksAddon = new WebLinksAddon();
    term.loadAddon(webLinksAddon);

    // Open terminal in container
    term.open(containerRef.current);

    // Initial fit
    setTimeout(() => {
      fitAddon.fit();
    }, 0);

    // Handle user input
    term.onData((data) => {
      sendMessage({ type: "input", data });
    });

    // Handle resize
    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });
    resizeObserver.observe(containerRef.current);

    // Also handle window resize
    window.addEventListener("resize", handleResize);

    // Connect to WebSocket
    connect();

    // Write welcome message
    term.writeln("\x1b[36m╔══════════════════════════════════════════╗\x1b[0m");
    term.writeln("\x1b[36m║      BLACK WALL TERMINAL INTERFACE       ║\x1b[0m");
    term.writeln("\x1b[36m╚══════════════════════════════════════════╝\x1b[0m");
    term.writeln("");
    term.writeln("\x1b[33mConnecting to server...\x1b[0m");
    term.writeln("");

    // Cleanup
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      resizeObserver.disconnect();
      window.removeEventListener("resize", handleResize);
      wsRef.current?.close(1000);
      term.dispose();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative w-full h-full">
      {/* Connection status indicator */}
      <div className="absolute top-2 right-2 z-10 flex items-center gap-2">
        <div
          className={`w-2 h-2 rounded-full ${
            connectionState === "connected"
              ? "bg-[var(--bw-success)] animate-pulse"
              : connectionState === "connecting"
                ? "bg-[var(--bw-warn)] animate-pulse"
                : connectionState === "error"
                  ? "bg-[var(--bw-danger)]"
                  : "bg-[var(--bw-muted)]"
          }`}
        />
        <span className="text-xs text-[var(--bw-muted)] uppercase">
          {connectionState}
        </span>
      </div>

      {/* Terminal container */}
      <div
        ref={containerRef}
        className="w-full h-full bg-[var(--bw-bg)] rounded"
        style={{
          // Ensure terminal fills container
          padding: "8px",
        }}
      />
    </div>
  );
}
